import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Spin } from 'antd';
import { AiFillCaretDown } from 'react-icons/ai';
import { useMutation } from '@tanstack/react-query';
import ConditionalRender from '../../shared/components/ConditionalRender';
import CustomWarning from '../../shared/components/CustomWarning';
import Notification from '../../shared/components/Notification';
import SearchBar from '../../shared/components/SearchBar';
import ClientGroupPanel from './components/ClientGroupPanel';
import ClientFormModal from './components/ClientFormModal';
import { FilterPanel } from './components/FilterPanel';
import { useClientGroups } from '../../hooks/useClientGroups';
import { useInfiniteScrollSentinel } from '../../hooks/useInfiniteScrollSentinel';
import { usePaymentGroups } from '../../hooks/usePaymentGroups';
import { useAddOns } from '../../hooks/useAddOns';
import { useClientFilterSelection } from './hooks/useClientFilterSelection';
import ClientManagerService, { getClientManagerErrorMessage } from '../../services/ClientManager/clientManager.service';
import { clientManagerServiceMessages } from '../../services/ClientManager/clientManager.constants';
import { NotificationTypes } from '../../enums/notificationTypes';
import { useSubscriptionTypes } from '../../hooks/useSubscriptionTypes';
import { ClientAction, ClientFormStep, EMPTY_COPY, subscriptionLabelFrom } from './constants';
import { SubscriptionTier } from '../../enums/clientEnums';
import { isClientFilterSelectionActive } from '../../types/ClientManagementTypes';
import type { ClientRecord } from '../../types/ClientManagementTypes';
import type { CheckboxFilterListOption } from '../../shared/components/CheckboxFilterList';
import styles from './ClientManager.module.scss';

const SEARCH_DEBOUNCE_MS = 500;
const DELETE_KEYWORD = 'DELETE';
const DELETE_WARNING_MESSAGE = 'This will permanently delete the client and client data.';
const DELETE_CONFIRM_MESSAGE = `Are you sure? To keep client data, consider archiving instead. If you wish to delete this client, type "${DELETE_KEYWORD}" in the field below.`;
const ARCHIVE_CONFIRM_MESSAGE = 'Archive this client? They move to the Archive group and can be restored later.';
const RESTORE_CONFIRM_MESSAGE = 'Restore this client? They move back to their payment organization.';

enum DeleteStep {
  WARNING = 'warning',
  KEYWORD = 'keyword',
}

interface DeleteFlow {
  client: ClientRecord;
  groupId: number;
  step: DeleteStep;
  anchor: HTMLElement | null;
  // Kept false through the close animation so the dialog exits where it opened.
  open: boolean;
}

// Archive and restore share one dialog; only the copy and the request differ.
interface ArchiveFlow {
  client: ClientRecord;
  isRestoring: boolean;
  anchor: HTMLElement | null;
  open: boolean;
}

// Which flow the wizard is serving. Kept through the close animation so the
// title doesn't change as the modal exits.
interface ClientFormFlow {
  isEditing: boolean;
  clientId?: number;
  initialStep: ClientFormStep;
  open: boolean;
}

interface ClientActionTarget {
  client: ClientRecord;
  groupId: number;
  anchor: HTMLElement | null;
}

// Optional: row actions carry a target, header actions don't.
type ClientActionHandler = (target?: ClientActionTarget) => void;

const ClientManager = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeGroupIds, setActiveGroupIds] = useState<Set<number>>(new Set());
  const [isAddIconActive, setIsAddIconActive] = useState(false);
  const [deleteFlow, setDeleteFlow] = useState<DeleteFlow | null>(null);
  const [archiveFlow, setArchiveFlow] = useState<ArchiveFlow | null>(null);
  const [formFlow, setFormFlow] = useState<ClientFormFlow | null>(null);

  const { groups, refreshGroups, isLoadingGroups, isGroupsLoadingError, groupsError, isFetchingNextGroups, hasNextGroups, fetchNextGroups } =
    useClientGroups(search);

  const { archiveClient, unarchiveClient, deleteClient } = ClientManagerService();
  const { mutateAsync: archiveClientAsync, isPending: isArchiving } = useMutation({ ...archiveClient() });
  const { mutateAsync: unarchiveClientAsync, isPending: isRestoring } = useMutation({ ...unarchiveClient() });
  const { mutateAsync: deleteClientAsync, isPending: isDeleting } = useMutation({ ...deleteClient() });

  const {
    draftSelection: draftFilterSelection,
    appliedSelection: appliedFilterSelection,
    isFilterActive,
    filterDropdownOpen,
    handleDraftSelectionChange,
    handleApplyFilters,
    handleClearAllFilters,
    handleFilterDropdownOpenChange,
  } = useClientFilterSelection();
  const { paymentGroups } = usePaymentGroups();
  const { addOns } = useAddOns();
  const { subscriptionTypesById } = useSubscriptionTypes();
  const subscriptionOptions: CheckboxFilterListOption[] = useMemo(
    () => Object.values(SubscriptionTier).map((tier) => ({ label: subscriptionLabelFrom(subscriptionTypesById, tier).toUpperCase(), value: tier })),
    [subscriptionTypesById],
  );
  const organisationOptions: CheckboxFilterListOption[] = useMemo(
    () => paymentGroups.map((group) => ({ label: group.name.toUpperCase(), value: String(group.id) })),
    [paymentGroups],
  );
  const addOnOptions: CheckboxFilterListOption[] = useMemo(
    () => addOns.map((addOn) => ({ label: addOn.name.toUpperCase(), value: String(addOn.id) })),
    [addOns],
  );

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The list stays mounted on failure; the toast is what reports it (Story 1 AC7).
  useEffect(() => {
    if (!isGroupsLoadingError) return;
    Notification({
      message: clientManagerServiceMessages.CLIENTS.GET_ERROR,
      description: getClientManagerErrorMessage(groupsError),
      type: NotificationTypes.ERROR,
    });
  }, [isGroupsLoadingError, groupsError]);

  // Narrowing by either search or filters is what makes a group worth opening.
  const isFiltered = !!search || isClientFilterSelectionActive(appliedFilterSelection);
  const query = JSON.stringify({ search, filters: appliedFilterSelection });

  // A match is auto-opened once and only once, so later pages expand as they
  // arrive while a group the user has since collapsed stays collapsed.
  const autoOpenedRef = useRef<{ query: string | null; ids: Set<number> }>({ query: null, ids: new Set() });
  useEffect(() => {
    if (isLoadingGroups) return;
    if (autoOpenedRef.current.query !== query) {
      autoOpenedRef.current = { query, ids: new Set() };
      setActiveGroupIds((ids) => (ids.size ? new Set() : ids));
    }
    if (!isFiltered) return;

    const freshIds = groups.map((group) => group.id).filter((id) => !autoOpenedRef.current.ids.has(id));
    if (!freshIds.length) return;
    freshIds.forEach((id) => autoOpenedRef.current.ids.add(id));
    setActiveGroupIds((ids) => new Set([...ids, ...freshIds]));
  }, [groups, isFiltered, isLoadingGroups, query]);

  const listSentinelRef = useInfiniteScrollSentinel({
    onReachEnd: fetchNextGroups,
    enabled: hasNextGroups && !isFetchingNextGroups,
  });

  const handleToggleGroup = useCallback((groupId: number) => {
    setActiveGroupIds((ids) => {
      const next = new Set(ids);
      if (!next.delete(groupId)) next.add(groupId);
      return next;
    });
  }, []);

  const closeDeleteFlow = useCallback(() => setDeleteFlow((flow) => (flow ? { ...flow, open: false } : flow)), []);
  const closeArchiveFlow = useCallback(() => setArchiveFlow((flow) => (flow ? { ...flow, open: false } : flow)), []);

  // Only discard once the dialog has finished closing -- the step change below
  // also closes a modal, and that must not drop the flow mid-way.
  const handleAfterClose = useCallback(() => setDeleteFlow((flow) => (flow && !flow.open ? null : flow)), []);
  const handleAfterArchiveClose = useCallback(() => setArchiveFlow((flow) => (flow && !flow.open ? null : flow)), []);

  const handleDeleteWarningConfirm = useCallback(() => {
    setDeleteFlow((flow) => (flow ? { ...flow, step: DeleteStep.KEYWORD } : flow));
  }, []);

  // The dialog stays open on failure so the toast's message is read next to the
  // action that produced it, and the admin can retry without reopening the menu.
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteFlow) return;
    try {
      await deleteClientAsync(deleteFlow.client.id);
      void refreshGroups();
      closeDeleteFlow();
    } catch (error) {
      Notification({
        message: clientManagerServiceMessages.CLIENTS.DELETE_ERROR,
        description: getClientManagerErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    }
  }, [deleteFlow, deleteClientAsync, refreshGroups, closeDeleteFlow]);

  const startDeleteFlow = useCallback((target?: ClientActionTarget) => {
    if (target) setDeleteFlow({ ...target, step: DeleteStep.WARNING, open: true });
  }, []);

  const startArchiveFlow = useCallback((target?: ClientActionTarget) => {
    if (target) setArchiveFlow({ client: target.client, anchor: target.anchor, isRestoring: false, open: true });
  }, []);

  const startRestoreFlow = useCallback((target?: ClientActionTarget) => {
    if (target) setArchiveFlow({ client: target.client, anchor: target.anchor, isRestoring: true, open: true });
  }, []);

  // Either direction moves the client between the Archive group and its payment
  // organization, so the collapse list itself changes and the whole list refetches.
  const handleArchiveConfirm = useCallback(async () => {
    if (!archiveFlow) return;
    const { client, isRestoring: restoring } = archiveFlow;
    try {
      await (restoring ? unarchiveClientAsync(client.id) : archiveClientAsync(client.id));
      void refreshGroups();
      closeArchiveFlow();
    } catch (error) {
      Notification({
        message: restoring ? clientManagerServiceMessages.CLIENTS.RESTORE_ERROR : clientManagerServiceMessages.CLIENTS.ARCHIVE_ERROR,
        description: getClientManagerErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    }
  }, [archiveFlow, archiveClientAsync, unarchiveClientAsync, refreshGroups, closeArchiveFlow]);

  const handleAddClient = useCallback(() => setFormFlow({ isEditing: false, initialStep: ClientFormStep.CLIENT, open: true }), []);

  const handleEditClient = useCallback((target?: ClientActionTarget) => {
    if (!target) return;
    setFormFlow({ isEditing: true, clientId: target.client.id, initialStep: ClientFormStep.SUMMARY, open: true });
  }, []);

  // Story 2: a row opens the profile, a pill or add-on the subscription page.
  const handleOpenClient = useCallback(
    (client: ClientRecord, step: ClientFormStep) => setFormFlow({ isEditing: true, clientId: client.id, initialStep: step, open: true }),
    [],
  );

  const closeFormFlow = useCallback(() => setFormFlow((flow) => (flow ? { ...flow, open: false } : flow)), []);

  // A new client can land in a group that isn't listed yet, so the whole list refetches.
  const handleClientSaved = useCallback(() => {
    void refreshGroups();
  }, [refreshGroups]);

  // Record rather than a switch: a new action fails to compile until it has a
  // handler here.
  const clientActionHandlers = useMemo<Record<ClientAction, ClientActionHandler>>(
    () => ({
      [ClientAction.ADD_CLIENT]: handleAddClient,
      [ClientAction.EDIT_CLIENT]: handleEditClient,
      [ClientAction.ARCHIVE_CLIENT]: startArchiveFlow,
      [ClientAction.RESTORE_CLIENT]: startRestoreFlow,
      [ClientAction.DELETE_CLIENT]: startDeleteFlow,
    }),
    [handleAddClient, handleEditClient, startArchiveFlow, startRestoreFlow, startDeleteFlow],
  );

  // Optional call: the menu hands back a plain string, so an unknown id no-ops.
  const handleClientAction = useCallback(
    (action: ClientAction, target?: ClientActionTarget) => clientActionHandlers[action]?.(target),
    [clientActionHandlers],
  );

  const handleRowAction = useCallback(
    (action: ClientAction, client: ClientRecord, groupId: number, anchor: HTMLElement | null) =>
      handleClientAction(action, { client, groupId, anchor }),
    [handleClientAction],
  );

  const listMessage = () => {
    if (isGroupsLoadingError) return isFiltered ? EMPTY_COPY.SEARCH_ERROR : EMPTY_COPY.LOAD_ERROR;
    return isFiltered ? EMPTY_COPY.NO_SEARCH_MATCHES : EMPTY_COPY.NO_GROUPS;
  };

  return (
    <div className={styles.layout}>
      <header className={styles.toolbar}>
        <Dropdown
          dropdownRender={() => (
            <FilterPanel
              selection={draftFilterSelection}
              onChange={handleDraftSelectionChange}
              onApply={handleApplyFilters}
              onClearAll={handleClearAllFilters}
              organisationOptions={organisationOptions}
              subscriptionOptions={subscriptionOptions}
              addOnOptions={addOnOptions}
            />
          )}
          trigger={['click']}
          placement="bottomLeft"
          open={filterDropdownOpen}
          onOpenChange={handleFilterDropdownOpenChange}
        >
          <button
            type="button"
            className={[
              styles.filtersButton,
              filterDropdownOpen ? styles.filtersButtonOpen : '',
              isFilterActive ? styles.filtersButtonActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-expanded={filterDropdownOpen}
          >
            Filters
            <AiFillCaretDown />
          </button>
        </Dropdown>
        <SearchBar value={searchInput} onChange={setSearchInput} className={styles.search} />
        <button
          type="button"
          className={styles.addButton}
          onClick={() => handleClientAction(ClientAction.ADD_CLIENT)}
          onMouseEnter={() => setIsAddIconActive(true)}
          onMouseLeave={() => setIsAddIconActive(false)}
          onMouseDown={() => setIsAddIconActive(true)}
          onMouseUp={() => setIsAddIconActive(true)}
        >
          <img src={isAddIconActive ? '/assets/upload-file-icon-active.svg' : '/assets/upload-file-icon.svg'} alt="" className={styles.addIcon} />
          Add New Client
        </button>
      </header>
      <div className={styles.groupList}>
        <div className={styles.groupListInner}>
          <ConditionalRender visible={isLoadingGroups} hideFallback>
            <div className={styles.listLoading}>
              <Spin />
            </div>
          </ConditionalRender>
          {groups.map((group) => (
            <ClientGroupPanel
              key={group.id}
              group={group}
              isOpen={activeGroupIds.has(group.id)}
              onToggle={handleToggleGroup}
              isFiltered={isFiltered}
              onAction={handleRowAction}
              onOpenClient={handleOpenClient}
            />
          ))}
          <ConditionalRender visible={!isLoadingGroups && groups.length === 0} hideFallback>
            <p className={`${styles.listMessage} ${isGroupsLoadingError ? styles.errorState : styles.emptyState}`}>{listMessage()}</p>
          </ConditionalRender>
          <div ref={listSentinelRef} className={styles.sentinel} aria-hidden="true" />
        </div>
      </div>
      <ClientFormModal
        open={!!formFlow?.open}
        onClose={closeFormFlow}
        isEditing={!!formFlow?.isEditing}
        clientId={formFlow?.clientId}
        initialStep={formFlow?.initialStep ?? ClientFormStep.CLIENT}
        onSaved={handleClientSaved}
      />
      <CustomWarning
        open={!!archiveFlow?.open}
        anchorEl={archiveFlow?.anchor}
        afterClose={handleAfterArchiveClose}
        onCancel={closeArchiveFlow}
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving || isRestoring}
        message={archiveFlow?.isRestoring ? RESTORE_CONFIRM_MESSAGE : ARCHIVE_CONFIRM_MESSAGE}
        cancelText="Cancel"
        confirmText={archiveFlow?.isRestoring ? 'Restore' : 'Archive'}
        variant="dark"
      />
      <CustomWarning
        open={!!deleteFlow?.open && deleteFlow.step === DeleteStep.WARNING}
        anchorEl={deleteFlow?.anchor}
        afterClose={handleAfterClose}
        onCancel={closeDeleteFlow}
        onConfirm={handleDeleteWarningConfirm}
        message={DELETE_WARNING_MESSAGE}
        cancelText="No, Go Back"
        confirmText="Yes, Delete"
        variant="dark"
      />
      <CustomWarning
        open={!!deleteFlow?.open && deleteFlow.step === DeleteStep.KEYWORD}
        anchorEl={deleteFlow?.anchor}
        afterClose={handleAfterClose}
        onCancel={closeDeleteFlow}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        message={DELETE_CONFIRM_MESSAGE}
        cancelText="Cancel"
        confirmText="Confirm Delete"
        confirmKeyword={DELETE_KEYWORD}
        keywordPlaceholder=""
        variant="dark"
        size="large"
      />
    </div>
  );
};

export default ClientManager;
